import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { BehaviorSignalBus } from '../contracts/signals';
import { BehaviorTrackerBase } from './BehaviorTrackerBase';

/** Alinhado ao legado: throttle fixo de 100ms. */
const SCROLL_THROTTLE_MS = 100;

export type ScrollTrackerOptions = {
  readonly signalBus?: BehaviorSignalBus;
};

interface ScrollState {
  maxScrollPercent: number;
  scrollEvents: number;
  scrollVelocityAvg: number;
  lastScrollAt: number;
}

export class ScrollTracker extends BehaviorTrackerBase {
  private state: ScrollState = {
    maxScrollPercent: 0,
    scrollEvents: 0,
    scrollVelocityAvg: 0,
    lastScrollAt: 0,
  };

  private throttleTimer: ReturnType<typeof setTimeout> | null = null;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private lastScrollY = 0;

  private lastScrollTime = 0;

  private velocitySum = 0;

  private crossedThresholds = new Set<number>();

  private lastDispatchedMaxPercent = -1;

  private lastDispatchAt = 0;

  private readonly signalBus: BehaviorSignalBus | undefined;

  constructor(context: PixelRuntimeContext, options?: ScrollTrackerOptions) {
    super(context);
    this.signalBus = options?.signalBus;
  }

  protected onInit(): void {
    if (!this.isWindowAvailable()) return;

    this.resetState();
    this.lastScrollY = window.scrollY;
    this.lastScrollTime = Date.now();

    this.addWindowListener('scroll', () => this.handleScroll(), {
      passive: true,
    });
  }

  protected onDestroy(): void {
    this.throttleTimer = this.clearManagedTimeout(this.throttleTimer);
    this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
  }

  private resetState(): void {
    this.state = {
      maxScrollPercent: 0,
      scrollEvents: 0,
      scrollVelocityAvg: 0,
      lastScrollAt: 0,
    };
    this.lastScrollY = 0;
    this.lastScrollTime = 0;
    this.velocitySum = 0;
    this.crossedThresholds.clear();
    this.lastDispatchedMaxPercent = -1;
    this.lastDispatchAt = 0;
  }

  private getMaxScrollPercent(): number {
    if (!this.isWindowAvailable() || !this.isDocumentAvailable()) {
      return 0;
    }

    const doc = document.documentElement;
    const bodyScroll = document.body ? document.body.scrollHeight : 0;
    const scrollHeight = Math.max(doc.scrollHeight, bodyScroll);
    const clientHeight = doc.clientHeight;

    if (scrollHeight <= clientHeight) return 100;

    const maxScroll = scrollHeight - clientHeight;
    const current = window.scrollY || window.pageYOffset;
    return Math.min(100, Math.round((current / maxScroll) * 100));
  }

  private canDispatch(minIntervalMs: number): boolean {
    return Date.now() - this.lastDispatchAt >= minIntervalMs;
  }

  private handleScroll(): void {
    if (!this.isWindowAvailable()) return;

    const { debounceMs } = this.context.config.modules.behavior.trackers.scroll;

    if (this.throttleTimer) return;

    this.throttleTimer = setTimeout(() => {
      this.throttleTimer = null;
      this.updateScrollState();
      this.maybeDispatchOnThresholdCrossing(debounceMs);

      this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.maybeDispatchOnScrollEnd(debounceMs);
      }, debounceMs);
    }, SCROLL_THROTTLE_MS);
  }

  private updateScrollState(): void {
    if (!this.isWindowAvailable()) return;

    const now = Date.now();
    const scrollY = window.scrollY || window.pageYOffset;

    const percent = this.getMaxScrollPercent();
    if (percent > this.state.maxScrollPercent) {
      this.state.maxScrollPercent = percent;
    }

    this.state.scrollEvents += 1;
    this.state.lastScrollAt = now;

    if (this.state.scrollEvents > 1 && this.lastScrollTime > 0) {
      const deltaSeconds = (now - this.lastScrollTime) / 1000;
      const deltaY = Math.abs(scrollY - this.lastScrollY);
      if (deltaSeconds > 0) {
        const velocity = deltaY / deltaSeconds;
        this.velocitySum += velocity;
        this.state.scrollVelocityAvg =
          this.velocitySum / (this.state.scrollEvents - 1);
      }
    }

    this.lastScrollY = scrollY;
    this.lastScrollTime = now;
  }

  private maybeDispatchOnThresholdCrossing(minIntervalMs: number): void {
    const { thresholds } = this.context.config.modules.behavior.trackers.scroll;
    const percent = this.state.maxScrollPercent;

    let crossedNew = false;
    for (const threshold of thresholds) {
      if (percent >= threshold && !this.crossedThresholds.has(threshold)) {
        this.crossedThresholds.add(threshold);
        crossedNew = true;
      }
    }

    if (crossedNew && this.canDispatch(minIntervalMs)) {
      this.emitScrollEvent();
    }
  }

  private maybeDispatchOnScrollEnd(minIntervalMs: number): void {
    if (this.state.maxScrollPercent === this.lastDispatchedMaxPercent) return;
    if (!this.canDispatch(minIntervalMs)) return;

    this.emitScrollEvent();
  }

  private emitScrollEvent(): void {
    this.dispatchEngagement({
      type: 'SCROLL',
      category: 'ENGAGEMENT',
      maxScrollPercent: this.state.maxScrollPercent,
      scrollEvents: this.state.scrollEvents,
      scrollVelocityAvg: this.state.scrollVelocityAvg,
      lastScrollAt: this.state.lastScrollAt,
    });

    this.signalBus?.publish(
      this.signalBus.createSignal('SCROLL', 'ScrollTracker', {
        maxScrollPercent: this.state.maxScrollPercent,
      })
    );

    this.lastDispatchedMaxPercent = this.state.maxScrollPercent;
    this.lastDispatchAt = Date.now();
  }
}
